import httpClient from '../http/http-client';
import {
  ISchedule,
  ICreateSchedule,
  IUpdateSchedule,
  ICreateScheduleAction,
  IScheduleAction,
  IEmailQueueEntry,
} from './schedule.model';

export default class ScheduleService {
  http;

  constructor() {
    this.http = httpClient;
  }

  // ── Schedules ───────────────────────────────────────────────────────────────

  async getAll(params?: { page?: number; perPage?: number }): Promise<{ count: number; rows: ISchedule[] }> {
    const query = new URLSearchParams();
    if (params?.page !== undefined) query.append('page', String(params.page));
    if (params?.perPage !== undefined) query.append('perPage', String(params.perPage));
    const qs = query.toString();
    return this.http.get(`schedule${qs ? `?${qs}` : ''}`)
      .then(r => r.data)
      .catch(err => { console.log(err); throw err; });
  }

  async getOne(id: string): Promise<ISchedule> {
    return this.http.get(`schedule/${id}`)
      .then(r => r.data)
      .catch(err => { console.log(err); throw err; });
  }

  async create(data: ICreateSchedule): Promise<ISchedule> {
    return this.http.post('schedule', data)
      .then(r => r.data)
      .catch(err => { console.log(err); throw err; });
  }

  async update(id: string, data: IUpdateSchedule): Promise<ISchedule> {
    return this.http.patch(`schedule/${id}`, data)
      .then(r => r.data)
      .catch(err => { console.log(err); throw err; });
  }

  async remove(id: string): Promise<void> {
    return this.http.delete(`schedule/${id}`)
      .then(r => r.data)
      .catch(err => { console.log(err); throw err; });
  }

  async pause(id: string): Promise<ISchedule> {
    return this.http.post(`schedule/${id}/pause`)
      .then(r => r.data)
      .catch(err => { console.log(err); throw err; });
  }

  async runNow(id: string): Promise<{ message: string; results: unknown[] }> {
    return this.http.post(`schedule/${id}/run-now`)
      .then(r => r.data)
      .catch(err => { console.log(err); throw err; });
  }

  // ── Actions ─────────────────────────────────────────────────────────────────

  async getActions(scheduleId: string): Promise<IScheduleAction[]> {
    return this.http.get(`schedule/${scheduleId}/actions`)
      .then(r => r.data)
      .catch(err => { console.log(err); throw err; });
  }

  async createAction(scheduleId: string, data: ICreateScheduleAction): Promise<IScheduleAction> {
    return this.http.post(`schedule/${scheduleId}/actions`, data)
      .then(r => r.data)
      .catch(err => { console.log(err); throw err; });
  }

  async updateAction(scheduleId: string, actionId: string, data: Partial<ICreateScheduleAction> & { active?: boolean }): Promise<IScheduleAction> {
    return this.http.patch(`schedule/${scheduleId}/actions/${actionId}`, data)
      .then(r => r.data)
      .catch(err => { console.log(err); throw err; });
  }

  async deleteAction(scheduleId: string, actionId: string): Promise<void> {
    return this.http.delete(`schedule/${scheduleId}/actions/${actionId}`)
      .then(r => r.data)
      .catch(err => { console.log(err); throw err; });
  }

  // ── Email Queue ─────────────────────────────────────────────────────────────

  async getEmailQueue(params?: {
    page?: number;
    perPage?: number;
    status?: string;
    sourceType?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<{ count: number; rows: IEmailQueueEntry[] }> {
    const query = new URLSearchParams();
    if (params?.page !== undefined) query.append('page', String(params.page));
    if (params?.perPage !== undefined) query.append('perPage', String(params.perPage));
    if (params?.status) query.append('status', params.status);
    if (params?.sourceType) query.append('sourceType', params.sourceType);
    if (params?.dateFrom) query.append('dateFrom', params.dateFrom);
    if (params?.dateTo) query.append('dateTo', params.dateTo);
    const qs = query.toString();
    return this.http.get(`email-queue${qs ? `?${qs}` : ''}`)
      .then(r => r.data)
      .catch(err => { console.log(err); throw err; });
  }

  async retryEmailQueueEntry(id: string): Promise<IEmailQueueEntry> {
    return this.http.post(`email-queue/${id}/retry`)
      .then(r => r.data)
      .catch(err => { console.log(err); throw err; });
  }

  async cancelEmailQueueEntry(id: string): Promise<void> {
    return this.http.delete(`email-queue/${id}`)
      .then(r => r.data)
      .catch(err => { console.log(err); throw err; });
  }
}
